import React, { useState, useEffect, useRef } from "react";
import apiClient from "../api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../Components/ui/card";
import { Button } from "../Components/ui/button";
import { Input } from "../Components/ui/input";
import { Badge } from "../Components/ui/badge";
import { Loader2, Scan, CheckCircle2, AlertCircle, ArrowRight, Box, Nfc, ChevronLeft } from "lucide-react";
import { useToast } from "../context/ToastContext";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../Components/ui/select";
import { cn } from "../utils/utils";

export default function AdminLinkNFC() {
    const { addToast } = useToast();
    const queryClient = useQueryClient();

    // State
    const [selectedDonationId, setSelectedDonationId] = useState("");
    const [selectedItemId, setSelectedItemId] = useState(""); // Which item line are we tagging?
    const [isScanning, setIsScanning] = useState(false);
    const [scannedTags, setScannedTags] = useState([]);
    const [nfcError, setNfcError] = useState(null);
    const scannedTagsRef = useRef([]);
    const [currentInput, setCurrentInput] = useState("");
    const inputRef = useRef(null);

    // Fetch Received Donations
    const { data: donations = [], isLoading: donationsLoading } = useQuery({
        queryKey: ['receivedDonations'],
        queryFn: async () => {
            // Fetch all donations that are 'received' or 'pending' (if we allow tagging early)
            // User said "once the donated items are marked as received... admin should scan"
            const { data } = await apiClient.get('/admin/donations');
            // Filter filtering on client side or backend? The current endpoint returns everything.
            // Let's filter for now.
            // Filter out fully tagged donations
            const filtered = data.filter(d => {
                if (d.status !== 'received') return false;

                // Check if ALL items are fully tagged
                const allItemsTagged = d.items?.every(item => {
                    const taggedCount = item.nfc_ids ? item.nfc_ids.length : 0;
                    return taggedCount >= (item.quantity || 0);
                });

                return !allItemsTagged; // Only include if NOT fully tagged
            });
            return filtered;
        }
    });

    const selectedDonation = donations.find(d => d._id === selectedDonationId);
    const selectedItem = selectedDonation?.items.find(i => i._id === selectedItemId);

    // Mutation to Register Tags
    const registerTagsMutation = useMutation({
        mutationFn: async (tags) => {
            const { data } = await apiClient.post('/admin/nfc/register', {
                donationId: selectedDonationId,
                itemId: selectedItemId,
                nfcTags: tags
            });
            return data;
        },
        onSuccess: () => {
            addToast(`Successfully registered ${scannedTags.length} tags.`, "success");
            setIsScanning(false);
            setScannedTags([]);
            queryClient.invalidateQueries(['receivedDonations']);
        },
        onError: (err) => {
            addToast(err.response?.data?.msg || "Failed to register tags", "error");
        }
    });

    // --- NFC LOGIC (Reused) ---
    const startNfcReader = async () => {
        try {
            // eslint-disable-next-line no-undef
            const ndef = new window.NDEFReader();
            await ndef.scan();
            setNfcError(null);

            ndef.addEventListener("reading", ({ serialNumber }) => {
                if (serialNumber) {
                    const tagId = serialNumber.toUpperCase().replace(/:/g, '');
                    handleScanDirect(tagId);
                }
            });
        } catch (err) {
            console.log("NFC Error:", err);
            setNfcError(err.message || "NFC Access Denied");
        }
    };

    const handleScanDirect = (val) => {
        // Check duplicates in current session
        if (scannedTagsRef.current.includes(val)) {
            addToast("Tag already scanned in this session", "warning");
            return;
        }
        // Check validation against item quantity?
        if (selectedItem) {
            // alreadyRegistered count?
            const currentCount = selectedItem.nfc_ids ? selectedItem.nfc_ids.length : 0;
            if ((currentCount + scannedTagsRef.current.length + 1) > selectedItem.quantity) {
                addToast("Cannot scan more tags than item quantity!", "error");
                return;
            }
        }

        setScannedTags(prev => [...prev, val]);
        addToast("Tag Scanned", "success");
    };

    useEffect(() => {
        if (isScanning && 'NDEFReader' in window) {
            startNfcReader();
        }
    }, [isScanning]);

    useEffect(() => {
        scannedTagsRef.current = scannedTags;
    }, [scannedTags]);

    const handleScanSubmit = (e) => {
        e.preventDefault();
        const val = currentInput.trim();
        if (!val) return;
        handleScanDirect(val);
        setCurrentInput("");
    };
    // ---------------------------

    const handleComplete = () => {
        registerTagsMutation.mutate(scannedTags);
    };

    // --- RENDER: SCANNING MODE ---
    if (isScanning && selectedItem) {
        return (
            <div className="fixed inset-0 z-50 bg-black text-white flex flex-col overflow-hidden">
                {/* Header */}
                <div className="p-6 flex justify-between items-start">
                    <Button variant="ghost" onClick={() => setIsScanning(false)} className="text-zinc-400">
                        <ChevronLeft className="w-6 h-6 mr-2" /> Cancel
                    </Button>
                    <div className="text-right">
                        <h2 className="text-2xl font-bold">{selectedItem.name}</h2>
                        <p className="text-zinc-500">Scanning {scannedTags.length} / {selectedItem.quantity - (selectedItem.nfc_ids?.length || 0)} new tags</p>
                    </div>
                </div>

                {/* NFC Warning */}
                {nfcError && (
                    <div className="max-w-md mx-auto bg-red-900/20 border border-red-500/50 p-4 rounded-lg text-red-500 mb-4">
                        NFC Error: {nfcError}. Ensure HTTPS/Chrome Android.
                    </div>
                )}

                {/* Input Area */}
                <div className="flex-1 flex flex-col items-center justify-center p-6">
                    <div className="w-full max-w-md">
                        <form onSubmit={handleScanSubmit} className="relative">
                            <Input
                                value={currentInput}
                                onChange={e => setCurrentInput(e.target.value)}
                                placeholder="Scan Tag or Type ID..."
                                className="bg-zinc-900 border-zinc-700 text-white text-3xl font-mono py-8 pl-4 pr-4"
                                autoFocus
                            />
                        </form>
                        <p className="text-center text-zinc-500 mt-4 text-sm">Use NFC Reader or USB Scanner</p>
                    </div>

                    <div className="mt-8 w-full max-w-md space-y-2 max-h-[300px] overflow-y-auto">
                        {scannedTags.map((t, i) => (
                            <div key={i} className="bg-zinc-800 p-3 rounded flex justify-between font-mono">
                                <span>{t}</span>
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-6 bg-zinc-900 border-t border-zinc-800">
                    <Button
                        size="lg"
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        onClick={handleComplete}
                        disabled={scannedTags.length === 0 || registerTagsMutation.isPending}
                    >
                        {registerTagsMutation.isPending ? <Loader2 className="animate-spin mr-2" /> : <Box className="mr-2" />}
                        Register {scannedTags.length} Tags
                    </Button>
                </div>
            </div>
        );
    }

    // --- RENDER: DASHBOARD MODE ---
    return (
        <div className="p-6 md:p-10 space-y-8 animate-in fade-in duration-500">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-zinc-900 dark:text-white">Link NFC Tags</h1>
                    <p className="text-zinc-500 mt-1">Register permanent NFC ID's for received donation items.</p>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-full">
                    <Nfc className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>1. Select Donation</CardTitle>
                    <CardDescription>Choose a donation that has arrived at the warehouse.</CardDescription>
                </CardHeader>
                <CardContent>
                    <Select value={selectedDonationId} onValueChange={setSelectedDonationId}>
                        <SelectTrigger className="w-full text-lg py-6 h-auto">
                            <SelectValue placeholder="Select Received Donation...">
                                {selectedDonation ? (
                                    <div className="flex flex-col text-left">
                                        <span className="font-bold">{selectedDonation.donationId || selectedDonation._id}</span>
                                        <span className="text-xs text-zinc-400 font-normal">
                                            {selectedDonation.donation_type} from {selectedDonation.donor?.name || 'Unknown'}
                                        </span>
                                    </div>
                                ) : null}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {donations.map(d => (
                                <SelectItem key={d._id} value={d._id}>
                                    <div className="flex flex-col">
                                        <span className="font-bold">{d.donationId}</span>
                                        <span className="text-xs text-zinc-400">{d.donation_type} from {d.donor?.name || 'Unknown'}</span>
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </CardContent>
            </Card>

            {selectedDonation && (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {selectedDonation.items.map((item) => {
                        const totalProxy = item.quantity;
                        const registeredCount = item.nfc_ids ? item.nfc_ids.length : 0;
                        const isComplete = registeredCount >= totalProxy;

                        return (
                            <Card key={item._id} className={cn("relative overflow-hidden transition-all hover:border-blue-500", isComplete ? "opacity-75 bg-zinc-50 dark:bg-zinc-900" : "hover:shadow-lg")}>
                                {isComplete && (
                                    <div className="absolute top-2 right-2 text-green-500">
                                        <CheckCircle2 className="w-6 h-6" />
                                    </div>
                                )}
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg flex justify-between items-center">
                                        {item.name}
                                    </CardTitle>
                                    <CardDescription>{item.category}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm font-medium">
                                            <span>NFC Coverage</span>
                                            <span>{registeredCount} / {totalProxy}</span>
                                        </div>
                                        <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-blue-600 rounded-full transition-all duration-500"
                                                style={{ width: `${(registeredCount / totalProxy) * 100}%` }}
                                            />
                                        </div>
                                    </div>

                                    <Button
                                        className="w-full"
                                        disabled={isComplete}
                                        onClick={() => {
                                            setSelectedItemId(item._id);
                                            setIsScanning(true);
                                            setScannedTags([]);
                                        }}
                                    >
                                        {isComplete ? "Fully Tagged" : "Scan Tags"}
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

        </div>
    );
}
